import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { raidApi, type CategoriaRaid, type ContextoTipo, type CriarItemRaidInput } from '../../api/raid.js';
import { mensagemDeErro } from '../../mensagens/mensagens.js';
import { ErroRequisicao } from '../../api/cliente.js';

const CATEGORIAS: CategoriaRaid[] = ['Risk', 'Assumption', 'Issue', 'Dependency'];
const ESCALA: (1 | 2 | 3 | 4 | 5)[] = [1, 2, 3, 4, 5];

// SPEC-UX §4.7 — formulário por categoria: campos específicos aparecem
// conforme a escolha; responsável e prazo travam o envio se vazios (I3).
export function FormularioCriarItemRaid({ contextoTipo, contextoId, onCriado }: { contextoTipo: ContextoTipo; contextoId: string; onCriado: () => void }) {
  const queryClient = useQueryClient();
  const [categoria, setCategoria] = useState<CategoriaRaid>('Risk');
  const [descricao, setDescricao] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [prazo, setPrazo] = useState('');
  const [probabilidade, setProbabilidade] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [impacto, setImpacto] = useState<1 | 2 | 3 | 4 | 5>(3);
  const [dataValidacao, setDataValidacao] = useState('');
  const [dependenciaId, setDependenciaId] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const criar = useMutation({
    mutationFn: (input: CriarItemRaidInput) => raidApi.criar(input),
    onSuccess: () => {
      setDescricao('');
      setResponsavel('');
      setPrazo('');
      queryClient.invalidateQueries({ queryKey: ['raid'] });
      onCriado();
    },
    onError: (e) => setErro(e instanceof ErroRequisicao ? mensagemDeErro(e.erro.codigo) : 'Não foi possível criar o item.'),
  });

  function submeter() {
    setErro(null);
    const base = { categoria, descricao, responsavel, prazo, contextoTipo, contextoId };
    if (categoria === 'Risk') criar.mutate({ ...base, probabilidade, impacto });
    else if (categoria === 'Assumption') criar.mutate({ ...base, dataValidacao });
    else if (categoria === 'Dependency') criar.mutate({ ...base, dependenciaId });
    else criar.mutate(base);
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submeter();
      }}
      className="flex flex-col gap-3 rounded-lg border border-borda bg-superficie p-4"
    >
      <h2 className="text-sm font-semibold text-ink">Novo item RAID</h2>

      <select value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaRaid)} className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink">
        {CATEGORIAS.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>

      <textarea
        required
        placeholder="Descrição"
        value={descricao}
        onChange={(e) => setDescricao(e.target.value)}
        rows={2}
        className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
      />
      <input
        required
        placeholder="Responsável"
        value={responsavel}
        onChange={(e) => setResponsavel(e.target.value)}
        className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
      />
      <input required type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} className="rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink" />

      {categoria === 'Risk' && (
        <div className="flex gap-3">
          <label className="flex-1 text-xs text-grafite">
            Probabilidade
            <select value={probabilidade} onChange={(e) => setProbabilidade(Number(e.target.value) as never)} className="mt-1 w-full rounded border border-borda bg-base-100 px-2 py-1.5 text-sm text-ink">
              {ESCALA.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label className="flex-1 text-xs text-grafite">
            Impacto
            <select value={impacto} onChange={(e) => setImpacto(Number(e.target.value) as never)} className="mt-1 w-full rounded border border-borda bg-base-100 px-2 py-1.5 text-sm text-ink">
              {ESCALA.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {categoria === 'Assumption' && (
        <label className="text-xs text-grafite">
          Data de validação
          <input
            required
            type="date"
            value={dataValidacao}
            onChange={(e) => setDataValidacao(e.target.value)}
            className="mt-1 w-full rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
          />
        </label>
      )}

      {categoria === 'Dependency' && (
        <label className="text-xs text-grafite">
          ID da dependência
          <input
            required
            value={dependenciaId}
            onChange={(e) => setDependenciaId(e.target.value)}
            className="mt-1 w-full rounded border border-borda bg-base-100 px-3 py-2 text-sm text-ink"
          />
        </label>
      )}

      {erro && <p className="text-sm text-status-vermelho">{erro}</p>}

      <button type="submit" disabled={criar.isPending} className="self-start rounded bg-aco px-4 py-2 text-sm font-medium text-white hover:bg-aco-700 disabled:opacity-50">
        {criar.isPending ? 'Criando…' : 'Criar item'}
      </button>
    </form>
  );
}
